import { useMutation } from "@tanstack/react-query";
import { Link, useRouter } from "@tanstack/react-router";
import { useCallback } from "react";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { useAppForm } from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import { authClient, signUp } from "@/lib/auth-client";
import * as m from "@/paraglide/messages";

import { GoogleIcon } from "../icons/google";

type SignUpFormValues = {
	name: string;
	email: string;
	password: string;
	confirmPassword: string;
};

const defaultValues: SignUpFormValues = {
	name: "",
	email: "",
	password: "",
	confirmPassword: "",
};

export function SignupForm({
	redirectTo,
	...props
}: React.ComponentProps<typeof Card> & { redirectTo?: string }) {
	const signUpSchema = z
		.object({
			name: z.string().min(1, m.auth_nameRequired()),
			email: z.email(m.auth_emailInvalid()),
			password: z.string().min(8, m.auth_passwordMinLength()),
			confirmPassword: z.string().min(1, m.auth_confirmPasswordRequired()),
		})
		.refine((data) => data.password === data.confirmPassword, {
			message: m.auth_passwordMismatch(),
			path: ["confirmPassword"],
		});

	const callbackURL = redirectTo ?? "/";

	const googleSignUpMutation = useMutation({
		mutationKey: ["auth", "sign-up-google"],
		mutationFn: async () => {
			return await authClient.signIn.social(
				{
					provider: "google",
					callbackURL: callbackURL,
				},
				{ throw: true },
			);
		},
	});

	const signUpMutation = useMutation({
		mutationKey: ["auth", "sign-up"],
		mutationFn: async (values: SignUpFormValues) => {
			const result = await signUp.email({
				name: values.name,
				email: values.email,
				password: values.password,
				callbackURL: callbackURL,
			});

			if (result.error) {
				throw new Error(result.error.message ?? m.auth_signUpFailed());
			}

			return result.data;
		},
		meta: {
			success: { message: m.auth_signUpSuccess() },
		},
	});

	const form = useAppForm({
		validators: { onChange: signUpSchema },
		defaultValues,
		onSubmit: ({ value }) => {
			return signUpMutation.mutateAsync(value);
		},
	});

	const handleSubmit = useCallback(
		(e: React.SubmitEvent) => {
			e.preventDefault();
			e.stopPropagation();
			form.handleSubmit();
		},
		[form],
	);

	return (
		<Card {...props}>
			<CardHeader>
				<CardTitle>{m.auth_createAccountTitle()}</CardTitle>
				<CardDescription>{m.auth_createAccountDesc()}</CardDescription>
			</CardHeader>
			<CardContent>
				<form.AppForm>
					<form className="space-y-4" onSubmit={handleSubmit}>
						<form.AppField name="name">
							{(field) => (
								<field.TextField
									label={m.auth_fullName()}
									placeholder={m.auth_fullNamePlaceholder()}
									autoComplete="name"
									required
								/>
							)}
						</form.AppField>
						<form.AppField name="email">
							{(field) => (
								<field.TextField
									label={m.auth_email()}
									type="email"
									placeholder="m@example.com"
									autoComplete="email"
									required
								/>
							)}
						</form.AppField>
						<form.AppField name="password">
							{(field) => (
								<field.PasswordField
									label={m.auth_password()}
									placeholder={m.auth_passwordPlaceholder()}
									autoComplete="new-password"
									required
								/>
							)}
						</form.AppField>
						<form.AppField name="confirmPassword">
							{(field) => (
								<field.PasswordField
									label={m.auth_confirmPassword()}
									placeholder={m.auth_confirmPasswordPlaceholder()}
									autoComplete="new-password"
									required
								/>
							)}
						</form.AppField>
						<div className="flex flex-col gap-2">
							<form.SubmitButton
								className="w-full"
								disabled={signUpMutation.isPending}
							>
								{signUpMutation.isPending
									? m.auth_creatingAccount()
									: m.auth_createAccount()}
							</form.SubmitButton>
							<div className="relative">
								<div className="absolute inset-0 flex items-center">
									<Separator className="w-full" />
								</div>
								<div className="relative flex justify-center text-xs uppercase">
									<span className="bg-card text-muted-foreground px-2">
										{m.auth_orContinueWith()}
									</span>
								</div>
							</div>
							<Button
								variant="outline"
								type="button"
								className="w-full"
								disabled={googleSignUpMutation.isPending}
								onClick={() => googleSignUpMutation.mutate()}
							>
								<GoogleIcon className="mr-2 h-4 w-4" />
								{googleSignUpMutation.isPending
									? m.auth_redirecting()
									: m.auth_continueWithGoogle()}
							</Button>
						</div>
						<p className="text-muted-foreground text-center text-sm">
							{m.auth_hasAccount()}{" "}
							<Link to="/sign-in" className="underline underline-offset-4">
								{m.auth_signIn()}
							</Link>
						</p>
					</form>
				</form.AppForm>
			</CardContent>
		</Card>
	);
}
